/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { PortalMessage, UserProfile, NotificationPreferences } from '../../types';
import { 
  Mail, 
  Send, 
  CheckCircle2, 
  AlertCircle, 
  Filter, 
  Search, 
  Eye, 
  User, 
  Users, 
  Bell, 
  ShieldAlert, 
  ShieldCheck, 
  Clock, 
  Sparkles, 
  RefreshCw, 
  Copy, 
  ExternalLink, 
  X, 
  ChevronDown, 
  Sliders, 
  Tag, 
  Settings, 
  Globe, 
  Inbox,
  AlertTriangle,
  FileText,
  Building,
  GraduationCap,
  Calendar,
  Lock,
  Layers,
  ArrowRight,
  Check
} from 'lucide-react';
import { useInAppDialog } from '../Common/InAppDialog';

// Category color and label metadata
const CATEGORY_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  ferpaConsentAlerts: { 
    label: 'FERPA Consent', 
    color: 'text-emerald-700', 
    bg: 'bg-emerald-50', 
    border: 'border-emerald-200' 
  },
  newUserRegistrationAlerts: { 
    label: 'New User Signup', 
    color: 'text-blue-700', 
    bg: 'bg-blue-50', 
    border: 'border-blue-200' 
  },
  opportunityCreatedAlerts: { 
    label: 'Opportunity Alert', 
    color: 'text-indigo-700', 
    bg: 'bg-indigo-50', 
    border: 'border-indigo-200' 
  },
  applicationAlerts: { 
    label: 'Application / Placement', 
    color: 'text-sky-700', 
    bg: 'bg-sky-50', 
    border: 'border-sky-200' 
  },
  hoursApprovalAlerts: { 
    label: 'Hours Approval', 
    color: 'text-amber-700', 
    bg: 'bg-amber-50', 
    border: 'border-amber-200' 
  },
  evaluationAlerts: { 
    label: 'Evaluation / Mastery', 
    color: 'text-purple-700', 
    bg: 'bg-purple-50', 
    border: 'border-purple-200' 
  },
  systemSecurityAlerts: { 
    label: 'Security & Auth', 
    color: 'text-rose-700', 
    bg: 'bg-rose-50', 
    border: 'border-rose-200' 
  },
  standardAlerts: { 
    label: 'Platform Notice', 
    color: 'text-slate-700', 
    bg: 'bg-slate-50', 
    border: 'border-slate-200' 
  }
};

const formatTimestampEST = (timestampStr: string): string => {
  if (!timestampStr) return 'Not recorded';
  try {
    let dateStr = timestampStr;
    if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-')) {
      dateStr += 'Z';
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return timestampStr;

    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' EST';
  } catch {
    return timestampStr;
  }
};

const formatRelativeTime = (timestampStr: string): string => {
  if (!timestampStr) return '';
  try {
    let dateStr = timestampStr;
    if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-')) {
      dateStr += 'Z';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const diffMs = Date.now() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

export const NotificationsTabContent: React.FC = () => {
  const { 
    messages, 
    users, 
    tenants, 
    activeUser, 
    updateUserProfile, 
    sendNotificationEmail 
  } = useWBL();

  const { showToast, showAlert } = useInAppDialog();

  // Primary workspace mode
  const [activeSubTab, setActiveSubTab] = useState<'LOG' | 'MATRIX'>('LOG');

  // Filters for Email Dispatch Log
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [dateOrder, setDateOrder] = useState<'desc' | 'asc'>('desc');

  // Filters for User Subscriptions Matrix
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userRegionFilter, setUserRegionFilter] = useState('all');
  const [userEmailStatusFilter, setUserEmailStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all');

  // Modals & Inspection state
  const [inspectingMessage, setInspectingMessage] = useState<PortalMessage | null>(null);
  const [previewTab, setPreviewTab] = useState<'rendered' | 'text' | 'preferences' | 'raw'>('rendered');
  const [editingUserPrefs, setEditingUserPrefs] = useState<UserProfile | null>(null);
  const [tempPrefs, setTempPrefs] = useState<NotificationPreferences | null>(null);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);

  // Send Test Email modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [testRecipientEmail, setTestRecipientEmail] = useState('');
  const [testCategory, setTestCategory] = useState<keyof NotificationPreferences>('ferpaConsentAlerts');
  const [testSubject, setTestSubject] = useState('WBL Test Notification Verification');
  const [testMessage, setTestMessage] = useState('This is a test notification dispatched from the Superadmin Notifications Console to verify email deliverability and user preference enforcement.');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Copy indicator
  const [hasCopied, setHasCopied] = useState(false);

  // Quick lookup map: user by email
  const usersByEmail = useMemo(() => {
    const map = new Map<string, UserProfile>();
    (users || []).forEach(u => {
      if (u.email) {
        map.set(u.email.toLowerCase().trim(), u);
      }
    });
    return map;
  }, [users]);

  // Enriched Messages List
  const enrichedMessages = useMemo(() => {
    return (messages || []).map(msg => {
      const emailLower = (msg.toEmail || '').toLowerCase().trim();
      const matchedUser = usersByEmail.get(emailLower);
      const recipientName = msg.recipientName || matchedUser?.name || '';
      const recipientRole = msg.recipientRole || matchedUser?.role || 'external';
      const category = msg.category || 'standardAlerts';
      const deliveryStatus = msg.deliveryStatus || 'delivered';

      return {
        ...msg,
        recipientName,
        recipientRole,
        category,
        deliveryStatus
      };
    });
  }, [messages, usersByEmail]);

  // Filtered messages list
  const filteredMessages = useMemo(() => {
    return enrichedMessages.filter(msg => {
      // 1. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const inEmail = (msg.toEmail || '').toLowerCase().includes(q);
        const inName = (msg.recipientName || '').toLowerCase().includes(q);
        const inSubject = (msg.subject || '').toLowerCase().includes(q);
        const inTitle = (msg.title || '').toLowerCase().includes(q);
        const inBody = (msg.bodyHtml || '').toLowerCase().includes(q);
        if (!inEmail && !inName && !inSubject && !inTitle && !inBody) return false;
      }

      // 2. Category Filter
      if (categoryFilter !== 'all' && msg.category !== categoryFilter) {
        return false;
      }

      // 3. Role Filter
      if (roleFilter !== 'all' && msg.recipientRole !== roleFilter) {
        return false;
      }

      // 4. Status Filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'delivered' && msg.deliveryStatus !== 'delivered') return false;
        if (statusFilter === 'simulated' && msg.deliveryStatus !== 'simulated') return false;
        if (statusFilter === 'suppressed' && msg.deliveryStatus !== 'suppressed') return false;
        if (statusFilter === 'portal_only' && msg.channel !== 'portal_only') return false;
      }

      // 5. Region Filter
      if (regionFilter !== 'all') {
        const msgTenant = msg.tenantId || 'global';
        if (msgTenant !== regionFilter) return false;
      }

      return true;
    }).sort((a, b) => {
      const timeA = new Date(a.sentAt || 0).getTime();
      const timeB = new Date(b.sentAt || 0).getTime();
      return dateOrder === 'desc' ? timeB - timeA : timeA - timeB;
    });
  }, [enrichedMessages, searchQuery, categoryFilter, roleFilter, statusFilter, regionFilter, dateOrder]);

  // Filtered Users for Subscriptions Matrix
  const filteredUsers = useMemo(() => {
    return (users || []).filter(u => {
      if (!u || !u.uid) return false;

      // Search Query
      if (userSearchQuery.trim()) {
        const q = userSearchQuery.toLowerCase().trim();
        const inName = (u.name || '').toLowerCase().includes(q);
        const inEmail = (u.email || '').toLowerCase().includes(q);
        const inSchool = (u.school || '').toLowerCase().includes(q);
        const inDistrict = (u.district || '').toLowerCase().includes(q);
        const inOrg = (u.orgName || '').toLowerCase().includes(q);
        if (!inName && !inEmail && !inSchool && !inDistrict && !inOrg) return false;
      }

      // Role Filter
      if (userRoleFilter !== 'all' && u.role !== userRoleFilter) {
        return false;
      }

      // Region Filter
      if (userRegionFilter !== 'all') {
        const userTenant = u.tenantId || 'global';
        const userRegions = [userTenant, ...(u.extendedRegions || [])].filter(Boolean);
        if (!userRegions.includes(userRegionFilter)) return false;
      }

      // Email Status Filter
      if (userEmailStatusFilter !== 'all') {
        const isEmailEnabled = u.notificationPrefs?.emailNotifications !== false;
        if (userEmailStatusFilter === 'enabled' && !isEmailEnabled) return false;
        if (userEmailStatusFilter === 'disabled' && isEmailEnabled) return false;
      }

      return true;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [users, userSearchQuery, userRoleFilter, userRegionFilter, userEmailStatusFilter]);

  // Statistics calculation
  const stats = useMemo(() => {
    const total = enrichedMessages.length;
    const delivered = enrichedMessages.filter(m => m.deliveryStatus === 'delivered').length;
    const simulated = enrichedMessages.filter(m => m.deliveryStatus === 'simulated').length;
    const suppressed = enrichedMessages.filter(m => m.deliveryStatus === 'suppressed').length;
    const uniqueRecipients = new Set(enrichedMessages.map(m => (m.toEmail || '').toLowerCase()).filter(Boolean)).size;

    const optOutCount = (users || []).filter(u => u.notificationPrefs?.emailNotifications === false).length;

    return {
      total,
      delivered,
      simulated,
      suppressed,
      uniqueRecipients,
      optOutCount
    };
  }, [enrichedMessages, users]);

  // Handle opening user preferences editor
  const handleOpenUserPrefs = (user: UserProfile) => {
    setEditingUserPrefs(user);
    setTempPrefs({
      emailNotifications: user.notificationPrefs?.emailNotifications ?? true,
      inAppNotifications: user.notificationPrefs?.inAppNotifications ?? true,
      instantEmailAlerts: user.notificationPrefs?.instantEmailAlerts ?? true,
      nightlyBlackout: user.notificationPrefs?.nightlyBlackout ?? false,
      schoolHourBlackout: user.notificationPrefs?.schoolHourBlackout ?? false,
      standardAlerts: user.notificationPrefs?.standardAlerts ?? true,
      ferpaConsentAlerts: user.notificationPrefs?.ferpaConsentAlerts ?? true,
      newUserRegistrationAlerts: user.notificationPrefs?.newUserRegistrationAlerts ?? true,
      opportunityCreatedAlerts: user.notificationPrefs?.opportunityCreatedAlerts ?? true,
      partnerOrgAlerts: user.notificationPrefs?.partnerOrgAlerts ?? true,
      applicationAlerts: user.notificationPrefs?.applicationAlerts ?? true,
      hoursApprovalAlerts: user.notificationPrefs?.hoursApprovalAlerts ?? true,
      evaluationAlerts: user.notificationPrefs?.evaluationAlerts ?? true,
      systemSecurityAlerts: user.notificationPrefs?.systemSecurityAlerts ?? true
    });
  };

  // Save updated user preferences
  const handleSaveUserPrefs = async () => {
    if (!editingUserPrefs || !tempPrefs) return;
    setIsSavingPrefs(true);
    try {
      await updateUserProfile(editingUserPrefs.uid, {
        notificationPrefs: tempPrefs
      });
      showToast({
        title: 'Preferences Updated',
        message: `Notification preferences for ${editingUserPrefs.email} have been saved.`,
        type: 'success'
      });
      setEditingUserPrefs(null);
    } catch (err: any) {
      showAlert({
        title: 'Update Error',
        message: err.message || 'Failed to update preferences.',
        type: 'danger'
      });
    } finally {
      setIsSavingPrefs(false);
    }
  };

  // Quick switch from Matrix to Log filtered for specific user
  const handleFilterToUserEmails = (email: string) => {
    setSearchQuery(email);
    setCategoryFilter('all');
    setRoleFilter('all');
    setStatusFilter('all');
    setActiveSubTab('LOG');
    showToast({
      title: 'Filter Applied',
      message: `Showing dispatched emails for ${email}`,
      type: 'info'
    });
  };

  // Handle sending test email
  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testRecipientEmail.trim()) {
      showAlert({ title: 'Validation Error', message: 'Recipient email address is required.', type: 'warning' });
      return;
    }

    setIsSendingTest(true);
    try {
      await sendNotificationEmail(
        testRecipientEmail.trim().toLowerCase(),
        testSubject.trim(),
        testSubject.trim(),
        `<p>${testMessage.trim().replace(/\n/g, '<br/>')}</p><div style="margin-top:16px;padding:12px;background:#f8fafc;border-radius:8px;font-size:12px;color:#64748b;"><strong>Audit Category:</strong> ${String(testCategory)}<br/><strong>Timestamp:</strong> ${new Date().toISOString()}</div>`,
        undefined,
        testCategory
      );

      showToast({
        title: 'Notification Dispatched',
        message: `Test email sent to ${testRecipientEmail.trim()}. Check the ledger below.`,
        type: 'success'
      });

      setShowTestModal(false);
    } catch (err: any) {
      showAlert({
        title: 'Dispatch Failed',
        message: err.message || 'Failed to dispatch test notification.',
        type: 'danger'
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  // Copy raw HTML
  const handleCopyHtml = (htmlContent: string) => {
    navigator.clipboard.writeText(htmlContent);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2000);
  };

  // Get recipient matched user for inspected message
  const inspectedRecipientUser = useMemo(() => {
    if (!inspectingMessage?.toEmail) return null;
    return usersByEmail.get(inspectingMessage.toEmail.toLowerCase().trim()) || null;
  }, [inspectingMessage, usersByEmail]);

  return (
    <div className="space-y-6 font-sans">
      {/* Top Banner Header */}
      <div className="bg-white rounded-3xl border border-slate-200/80 p-6 md:p-8 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-6">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="bg-[#0094c8]/10 text-[#0094c8] text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full font-mono flex items-center space-x-1">
                <Bell className="w-3 h-3" />
                <span>Superadmin Audit Console</span>
              </span>
              <span className="bg-slate-100 text-slate-600 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full font-mono">
                Real-Time SendGrid & Portal Feed
              </span>
            </div>
            <h1 className="text-2xl font-black text-[#011d3c] tracking-tight uppercase">
              Notifications & Email Dispatch Ledger
            </h1>
            <p className="text-xs text-slate-500 font-medium">
              Weed through dispatched emails, track delivery status, and audit user notification preferences across all roles and campuses.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setTestRecipientEmail(activeUser?.email || '');
                setShowTestModal(true);
              }}
              className="px-4 py-2.5 bg-[#0094c8] hover:bg-[#007ba6] text-white font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm flex items-center space-x-2 cursor-pointer"
            >
              <Send className="w-4 h-4" />
              <span>Send Test Notification</span>
            </button>
          </div>
        </div>

        {/* Live Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 pt-6">
          <div className="bg-slate-50/70 border border-slate-200/60 p-4 rounded-2xl">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block font-mono">Total Dispatched</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-black text-slate-900">{stats.total}</span>
              <Mail className="w-4 h-4 text-slate-400" />
            </div>
            <span className="text-[9px] text-slate-400 font-medium mt-1 block">Recorded in database</span>
          </div>

          <div className="bg-emerald-50/50 border border-emerald-100 p-4 rounded-2xl">
            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 block font-mono">Delivered Emails</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-black text-emerald-700">{stats.delivered}</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </div>
            <span className="text-[9px] text-emerald-600/80 font-medium mt-1 block">Live SendGrid dispatches</span>
          </div>

          <div className="bg-amber-50/50 border border-amber-100 p-4 rounded-2xl">
            <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 block font-mono">Simulated</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-black text-amber-700">{stats.simulated}</span>
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>
            <span className="text-[9px] text-amber-600/80 font-medium mt-1 block">Sandbox / dev runs</span>
          </div>

          <div className="bg-rose-50/50 border border-rose-100 p-4 rounded-2xl">
            <span className="text-[10px] font-black uppercase tracking-wider text-rose-600 block font-mono">Suppressed</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-black text-rose-700">{stats.suppressed}</span>
              <ShieldAlert className="w-4 h-4 text-rose-500" />
            </div>
            <span className="text-[9px] text-rose-600/80 font-medium mt-1 block">User opted out</span>
          </div>

          <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-2xl">
            <span className="text-[10px] font-black uppercase tracking-wider text-blue-600 block font-mono">Recipients</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-black text-blue-700">{stats.uniqueRecipients}</span>
              <Users className="w-4 h-4 text-blue-500" />
            </div>
            <span className="text-[9px] text-blue-600/80 font-medium mt-1 block">Unique accounts reached</span>
          </div>

          <div className="bg-slate-100/60 border border-slate-200/60 p-4 rounded-2xl">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block font-mono">Email Opt-Outs</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-2xl font-black text-slate-700">{stats.optOutCount}</span>
              <Sliders className="w-4 h-4 text-slate-400" />
            </div>
            <span className="text-[9px] text-slate-500 font-medium mt-1 block">Users disabled emails</span>
          </div>
        </div>

        {/* View Mode Ribbon */}
        <div className="flex items-center space-x-2 mt-6 pt-6 border-t border-slate-100">
          <button
            onClick={() => setActiveSubTab('LOG')}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center space-x-2 cursor-pointer ${
              activeSubTab === 'LOG'
                ? 'bg-[#011d3c] text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70'
            }`}
          >
            <Mail className="w-4 h-4" />
            <span>Email Dispatch Ledger ({filteredMessages.length})</span>
          </button>

          <button
            onClick={() => setActiveSubTab('MATRIX')}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center space-x-2 cursor-pointer ${
              activeSubTab === 'MATRIX'
                ? 'bg-[#011d3c] text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70'
            }`}
          >
            <Sliders className="w-4 h-4" />
            <span>User Subscriptions & Permissions ({filteredUsers.length})</span>
          </button>
        </div>
      </div>

      {/* ==================================================== */}
      {/* SUB-TAB 1: EMAIL DISPATCH LEDGER                     */}
      {/* ==================================================== */}
      {activeSubTab === 'LOG' && (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm space-y-6">
          {/* Filters Bar */}
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-3">
              {/* Search input */}
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by recipient email, name, subject, or content..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-[#0094c8] focus:border-transparent outline-none bg-slate-50/50"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-xs font-bold"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Category Filter */}
              <div className="relative w-full md:w-56">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-[#0094c8] outline-none uppercase"
                >
                  <option value="all">ALL CATEGORIES</option>
                  <option value="ferpaConsentAlerts">FERPA Consents</option>
                  <option value="newUserRegistrationAlerts">New Signups</option>
                  <option value="opportunityCreatedAlerts">Opportunities</option>
                  <option value="applicationAlerts">Applications & Placements</option>
                  <option value="hoursApprovalAlerts">Hours Approvals</option>
                  <option value="evaluationAlerts">Evaluations & Mastery</option>
                  <option value="systemSecurityAlerts">Security & Passwords</option>
                  <option value="standardAlerts">Platform Notices</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-3 pointer-events-none" />
              </div>

              {/* Recipient Role Filter */}
              <div className="relative w-full md:w-44">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-[#0094c8] outline-none uppercase"
                >
                  <option value="all">ALL ROLES</option>
                  <option value="student">Students</option>
                  <option value="advisor">Advisors</option>
                  <option value="organization">Employers</option>
                  <option value="regional_admin">Regional Admins</option>
                  <option value="superadmin">Superadmins</option>
                  <option value="parent">Parents</option>
                  <option value="external">External Contacts</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-3 pointer-events-none" />
              </div>

              {/* Status Filter */}
              <div className="relative w-full md:w-40">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-[#0094c8] outline-none uppercase"
                >
                  <option value="all">ALL STATUSES</option>
                  <option value="delivered">Delivered</option>
                  <option value="simulated">Simulated</option>
                  <option value="suppressed">Suppressed</option>
                  <option value="portal_only">In-Portal Only</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-3 pointer-events-none" />
              </div>

              {/* Date Sort */}
              <div className="relative w-full md:w-36">
                <select
                  value={dateOrder}
                  onChange={(e) => setDateOrder(e.target.value as any)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-[#0094c8] outline-none uppercase"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-3 pointer-events-none" />
              </div>
            </div>

            {/* Active filter count & clear all */}
            <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
              <span>
                Showing <strong className="text-slate-800">{filteredMessages.length}</strong> of{' '}
                <strong className="text-slate-800">{enrichedMessages.length}</strong> recorded notifications
              </span>
              {(searchQuery || categoryFilter !== 'all' || roleFilter !== 'all' || statusFilter !== 'all' || regionFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setCategoryFilter('all');
                    setRoleFilter('all');
                    setStatusFilter('all');
                    setRegionFilter('all');
                  }}
                  className="text-[#0094c8] hover:text-[#007ba6] font-bold text-xs flex items-center space-x-1 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Reset All Filters</span>
                </button>
              )}
            </div>
          </div>

          {/* Messages Table */}
          {filteredMessages.length === 0 ? (
            <div className="py-16 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50 space-y-3">
              <div className="w-12 h-12 bg-white rounded-2xl border border-slate-200 flex items-center justify-center mx-auto text-slate-400 shadow-xs">
                <Inbox className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">No matching notification emails found</h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                No outbound notifications match your current filter criteria. Try clearing search keywords or resetting filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200/80 rounded-2xl">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200/80 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                    <th className="py-3 px-4">Recipient</th>
                    <th className="py-3 px-4">Subject & Content</th>
                    <th className="py-3 px-4">Category</th>
                    <th className="py-3 px-4">Delivery Status</th>
                    <th className="py-3 px-4">Sent At</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredMessages.map((msg) => {
                    const catMeta = CATEGORY_META[msg.category || ''] || CATEGORY_META.standardAlerts;
                    const recipientUser = usersByEmail.get((msg.toEmail || '').toLowerCase().trim());

                    return (
                      <tr 
                        key={msg.id} 
                        className="hover:bg-slate-50/60 transition-colors group cursor-pointer"
                        onClick={() => {
                          setInspectingMessage(msg);
                          setPreviewTab('rendered');
                        }}
                      >
                        {/* Recipient */}
                        <td className="py-3.5 px-4 align-top">
                          <div className="flex items-start space-x-2.5">
                            <div className="w-7 h-7 rounded-lg bg-[#0094c8]/10 text-[#0094c8] font-black text-xs flex items-center justify-center shrink-0 uppercase mt-0.5">
                              {(msg.recipientName || msg.toEmail || 'U').charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-extrabold text-slate-900 truncate">
                                {msg.recipientName || msg.toEmail}
                              </p>
                              <p className="text-[11px] text-slate-500 font-mono truncate">
                                {msg.toEmail}
                              </p>
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
                                  {msg.recipientRole || 'User'}
                                </span>
                                {msg.tenantId && msg.tenantId !== 'global' && (
                                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-mono">
                                    {msg.tenantId}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Subject & Snippet */}
                        <td className="py-3.5 px-4 align-top max-w-md">
                          <p className="font-extrabold text-slate-800 text-xs tracking-tight line-clamp-1">
                            {msg.subject || 'Untitled Notification'}
                          </p>
                          <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">
                            {(msg.bodyHtml || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || 'No preview available'}
                          </p>
                        </td>

                        {/* Category */}
                        <td className="py-3.5 px-4 align-top whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase font-mono border ${catMeta.bg} ${catMeta.color} ${catMeta.border}`}>
                            {catMeta.label}
                          </span>
                        </td>

                        {/* Delivery Status */}
                        <td className="py-3.5 px-4 align-top whitespace-nowrap">
                          {msg.deliveryStatus === 'suppressed' ? (
                            <div>
                              <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase font-mono bg-rose-50 text-rose-700 border border-rose-200">
                                <ShieldAlert className="w-3 h-3 text-rose-500" />
                                <span>Suppressed</span>
                              </span>
                              {msg.suppressionReason && (
                                <p className="text-[9px] text-rose-600 mt-1 max-w-[160px] truncate" title={msg.suppressionReason}>
                                  {msg.suppressionReason}
                                </p>
                              )}
                            </div>
                          ) : msg.deliveryStatus === 'simulated' ? (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase font-mono bg-amber-50 text-amber-700 border border-amber-200">
                              <Sparkles className="w-3 h-3 text-amber-500" />
                              <span>Simulated</span>
                            </span>
                          ) : msg.channel === 'portal_only' ? (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase font-mono bg-indigo-50 text-indigo-700 border border-indigo-200">
                              <Inbox className="w-3 h-3 text-indigo-500" />
                              <span>Portal Only</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase font-mono bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                              <span>Delivered</span>
                            </span>
                          )}
                        </td>

                        {/* Sent At */}
                        <td className="py-3.5 px-4 align-top whitespace-nowrap text-slate-500 font-mono text-[11px]">
                          <div>{formatTimestampEST(msg.sentAt)}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5 font-sans">
                            {formatRelativeTime(msg.sentAt)}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 align-top text-right whitespace-nowrap">
                          <div className="flex items-center justify-end space-x-1.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                setInspectingMessage(msg);
                                setPreviewTab('rendered');
                              }}
                              className="px-2.5 py-1 text-[11px] font-bold text-[#0094c8] hover:bg-[#0094c8]/10 rounded-lg transition-colors flex items-center space-x-1"
                              title="Inspect Email"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>Inspect</span>
                            </button>

                            {recipientUser && (
                              <button
                                onClick={() => handleOpenUserPrefs(recipientUser)}
                                className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Audit User Notification Preferences"
                              >
                                <Sliders className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ==================================================== */}
      {/* SUB-TAB 2: USER SUBSCRIPTION & PERMISSION MATRIX     */}
      {/* ==================================================== */}
      {activeSubTab === 'MATRIX' && (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-base font-black text-slate-900 uppercase tracking-tight">
                User Notification Subscriptions & Eligibility Matrix
              </h2>
              <p className="text-xs text-slate-500">
                Determine what notifications and emails each individual user is authorized and configured to receive.
              </p>
            </div>
          </div>

          {/* User Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search user by name, email, high school, employer org..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-[#0094c8] outline-none bg-slate-50/50"
              />
            </div>

            <div className="relative w-full sm:w-44">
              <select
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value)}
                className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-[#0094c8] outline-none uppercase"
              >
                <option value="all">ALL ROLES</option>
                <option value="student">Students</option>
                <option value="advisor">Advisors</option>
                <option value="organization">Employers</option>
                <option value="regional_admin">Regional Admins</option>
                <option value="superadmin">Superadmins</option>
                <option value="parent">Parents</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-3 pointer-events-none" />
            </div>

            <div className="relative w-full sm:w-48">
              <select
                value={userEmailStatusFilter}
                onChange={(e) => setUserEmailStatusFilter(e.target.value as any)}
                className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-[#0094c8] outline-none uppercase"
              >
                <option value="all">ALL EMAIL STATES</option>
                <option value="enabled">Emails Active</option>
                <option value="disabled">Emails Disabled (Opted Out)</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-3 pointer-events-none" />
            </div>
          </div>

          {/* User Matrix Table */}
          <div className="overflow-x-auto border border-slate-200/80 rounded-2xl">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200/80 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="py-3 px-4">User Account</th>
                  <th className="py-3 px-4">Master Email</th>
                  <th className="py-3 px-4">In-Portal</th>
                  <th className="py-3 px-4">FERPA Alerts</th>
                  <th className="py-3 px-4">Signups</th>
                  <th className="py-3 px-4">Applications</th>
                  <th className="py-3 px-4">Hours & Evals</th>
                  <th className="py-3 px-4">Security</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map((u) => {
                  const prefs = u.notificationPrefs;
                  const isEmailOn = prefs?.emailNotifications !== false;
                  const isInAppOn = prefs?.inAppNotifications !== false;
                  const isFerpaOn = prefs?.ferpaConsentAlerts !== false;
                  const isSignupOn = prefs?.newUserRegistrationAlerts !== false;
                  const isAppOn = prefs?.applicationAlerts !== false;
                  const isHoursOn = prefs?.hoursApprovalAlerts !== false;
                  const isEvalOn = prefs?.evaluationAlerts !== false;
                  const isSecOn = prefs?.systemSecurityAlerts !== false;

                  return (
                    <tr key={u.uid} className="hover:bg-slate-50/60 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2.5">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 font-black text-xs flex items-center justify-center shrink-0 uppercase">
                            {(u.name || u.email || 'U').charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-extrabold text-slate-900 truncate">
                              {u.name || 'Unnamed User'}
                            </p>
                            <p className="text-[11px] text-slate-500 font-mono truncate">
                              {u.email}
                            </p>
                            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
                              {u.role}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Master Email Toggle */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase font-mono ${
                          isEmailOn ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}>
                          {isEmailOn ? 'Active' : 'Disabled'}
                        </span>
                      </td>

                      {/* In-Portal Toggle */}
                      <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px]">
                        <span className={isInAppOn ? 'text-emerald-600 font-bold' : 'text-slate-400'}>
                          {isInAppOn ? 'ON' : 'OFF'}
                        </span>
                      </td>

                      {/* FERPA */}
                      <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px]">
                        <span className={isFerpaOn ? 'text-emerald-600 font-bold' : 'text-slate-400'}>
                          {isFerpaOn ? 'ON' : 'OFF'}
                        </span>
                      </td>

                      {/* Signups */}
                      <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px]">
                        <span className={isSignupOn ? 'text-emerald-600 font-bold' : 'text-slate-400'}>
                          {isSignupOn ? 'ON' : 'OFF'}
                        </span>
                      </td>

                      {/* Applications */}
                      <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px]">
                        <span className={isAppOn ? 'text-emerald-600 font-bold' : 'text-slate-400'}>
                          {isAppOn ? 'ON' : 'OFF'}
                        </span>
                      </td>

                      {/* Hours & Evals */}
                      <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px]">
                        <span className={(isHoursOn && isEvalOn) ? 'text-emerald-600 font-bold' : 'text-slate-400'}>
                          {isHoursOn && isEvalOn ? 'BOTH ON' : isHoursOn ? 'HOURS' : isEvalOn ? 'EVALS' : 'OFF'}
                        </span>
                      </td>

                      {/* Security */}
                      <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px]">
                        <span className={isSecOn ? 'text-emerald-600 font-bold' : 'text-slate-400'}>
                          {isSecOn ? 'ON' : 'OFF'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleFilterToUserEmails(u.email || '')}
                            className="px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:text-[#0094c8] hover:bg-slate-100 rounded-lg transition-colors flex items-center space-x-1"
                            title="View all dispatched emails to this user"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            <span>Emails</span>
                          </button>

                          <button
                            onClick={() => handleOpenUserPrefs(u)}
                            className="px-2.5 py-1 text-[11px] font-bold text-[#0094c8] hover:bg-[#0094c8]/10 rounded-lg transition-colors flex items-center space-x-1"
                            title="Manage User Notification Preferences"
                          >
                            <Sliders className="w-3.5 h-3.5" />
                            <span>Edit</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* MODAL 1: DETAILED EMAIL INSPECTOR DRAWER / MODAL      */}
      {/* ==================================================== */}
      {inspectingMessage && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-slate-50/50">
              <div className="space-y-1 pr-6">
                <div className="flex items-center space-x-2">
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">
                    Message Audit ID: {inspectingMessage.id}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 font-mono">
                    {formatTimestampEST(inspectingMessage.sentAt)}
                  </span>
                </div>
                <h3 className="text-lg font-black text-slate-900 tracking-tight">
                  {inspectingMessage.subject}
                </h3>
                <p className="text-xs text-slate-500">
                  To: <strong className="text-slate-800">{inspectingMessage.toEmail}</strong> ({inspectingMessage.recipientName || 'Unregistered Name'})
                </p>
              </div>

              <button
                onClick={() => setInspectingMessage(null)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-200/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Sub-Tabs */}
            <div className="flex items-center space-x-2 px-6 pt-4 border-b border-slate-100 bg-white">
              <button
                onClick={() => setPreviewTab('rendered')}
                className={`pb-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                  previewTab === 'rendered'
                    ? 'border-[#0094c8] text-[#0094c8]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Rendered Email Preview
              </button>
              <button
                onClick={() => setPreviewTab('text')}
                className={`pb-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                  previewTab === 'text'
                    ? 'border-[#0094c8] text-[#0094c8]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Plain Text
              </button>
              <button
                onClick={() => setPreviewTab('preferences')}
                className={`pb-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                  previewTab === 'preferences'
                    ? 'border-[#0094c8] text-[#0094c8]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Recipient Subscriptions
              </button>
              <button
                onClick={() => setPreviewTab('raw')}
                className={`pb-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                  previewTab === 'raw'
                    ? 'border-[#0094c8] text-[#0094c8]'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                HTML Code
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
              {previewTab === 'rendered' && (
                <div className="space-y-4">
                  {/* Delivery Status Banner */}
                  <div className={`p-4 rounded-2xl border flex items-center justify-between text-xs ${
                    inspectingMessage.deliveryStatus === 'suppressed'
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : inspectingMessage.deliveryStatus === 'simulated'
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  }`}>
                    <div className="flex items-center space-x-2">
                      {inspectingMessage.deliveryStatus === 'suppressed' ? (
                        <ShieldAlert className="w-5 h-5 text-rose-600" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      )}
                      <div>
                        <p className="font-extrabold uppercase font-mono tracking-wider">
                          Status: {inspectingMessage.deliveryStatus?.toUpperCase()}
                        </p>
                        <p className="text-[11px] opacity-80">
                          {inspectingMessage.suppressionReason || 'Dispatched via verified SendGrid notification channel.'}
                        </p>
                      </div>
                    </div>

                    <span className="font-mono text-[10px] font-bold uppercase bg-white/60 px-2 py-0.5 rounded">
                      Channel: {inspectingMessage.channel || 'Email & Portal'}
                    </span>
                  </div>

                  {/* Sandboxed HTML Preview */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs overflow-hidden">
                    <div 
                      className="prose prose-sm max-w-none text-slate-800"
                      dangerouslySetInnerHTML={{ __html: inspectingMessage.bodyHtml || '<p>No content</p>' }}
                    />
                  </div>
                </div>
              )}

              {previewTab === 'text' && (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 font-mono text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {inspectingMessage.bodyHtml?.replace(/<[^>]*>/g, '') || 'No plain text'}
                </div>
              )}

              {previewTab === 'preferences' && (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 font-mono">
                    Recipient Account Alert Configuration
                  </h4>
                  {inspectedRecipientUser ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/60">
                          <span className="text-[10px] font-bold text-slate-400 uppercase font-mono">Master Email Notifications</span>
                          <p className={`text-sm font-black mt-0.5 ${inspectedRecipientUser.notificationPrefs?.emailNotifications !== false ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {inspectedRecipientUser.notificationPrefs?.emailNotifications !== false ? 'ENABLED' : 'DISABLED'}
                          </p>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/60">
                          <span className="text-[10px] font-bold text-slate-400 uppercase font-mono">In-Portal Notifications</span>
                          <p className={`text-sm font-black mt-0.5 ${inspectedRecipientUser.notificationPrefs?.inAppNotifications !== false ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {inspectedRecipientUser.notificationPrefs?.inAppNotifications !== false ? 'ENABLED' : 'DISABLED'}
                          </p>
                        </div>
                      </div>

                      <div className="border border-slate-200/80 rounded-xl p-4 space-y-2">
                        <span className="text-[10px] font-black uppercase text-slate-400 font-mono">Specific Category Eligibility</span>
                        <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                          {[
                            { key: 'ferpaConsentAlerts', label: 'FERPA Consents' },
                            { key: 'newUserRegistrationAlerts', label: 'New User Signups' },
                            { key: 'opportunityCreatedAlerts', label: 'Opportunities' },
                            { key: 'applicationAlerts', label: 'Applications' },
                            { key: 'hoursApprovalAlerts', label: 'Hours Approvals' },
                            { key: 'evaluationAlerts', label: 'Student Evaluations' },
                            { key: 'systemSecurityAlerts', label: 'Security Alerts' },
                          ].map(item => {
                            const isAllowed = inspectedRecipientUser.notificationPrefs?.[item.key as keyof NotificationPreferences] !== false;
                            return (
                              <div key={item.key} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                                <span className="font-medium text-slate-700">{item.label}</span>
                                <span className={`font-mono font-bold text-[10px] ${isAllowed ? 'text-emerald-600' : 'text-slate-400'}`}>
                                  {isAllowed ? 'ALLOWED' : 'OPTED OUT'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          handleOpenUserPrefs(inspectedRecipientUser);
                        }}
                        className="px-4 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm"
                      >
                        Modify Recipient Preferences
                      </button>
                    </div>
                  ) : (
                    <div className="py-8 text-center text-slate-500 text-xs">
                      This notification was addressed to an external contact (<code className="font-bold">{inspectingMessage.toEmail}</code>) without an active registered platform account.
                    </div>
                  )}
                </div>
              )}

              {previewTab === 'raw' && (
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleCopyHtml(inspectingMessage.bodyHtml || '')}
                      className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-lg transition-colors flex items-center space-x-1"
                    >
                      {hasCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{hasCopied ? 'Copied HTML!' : 'Copy Code'}</span>
                    </button>
                  </div>
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-2xl text-[11px] font-mono overflow-x-auto whitespace-pre-wrap max-h-96">
                    {inspectingMessage.bodyHtml}
                  </pre>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-between">
              <button
                onClick={() => handleFilterToUserEmails(inspectingMessage.toEmail)}
                className="px-3 py-1.5 text-slate-600 hover:text-[#0094c8] text-xs font-bold transition-colors flex items-center space-x-1"
              >
                <Search className="w-3.5 h-3.5" />
                <span>Filter all emails for {inspectingMessage.toEmail}</span>
              </button>

              <button
                onClick={() => setInspectingMessage(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* MODAL 2: USER PREFERENCE EDITING MODAL               */}
      {/* ==================================================== */}
      {editingUserPrefs && tempPrefs && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-slate-50/50">
              <div>
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-mono">
                  Superadmin Override
                </span>
                <h3 className="text-lg font-black text-slate-900 tracking-tight mt-1">
                  Edit Notification Subscriptions
                </h3>
                <p className="text-xs text-slate-500 font-mono">
                  {editingUserPrefs.name} ({editingUserPrefs.email})
                </p>
              </div>

              <button
                onClick={() => setEditingUserPrefs(null)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-200/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Master Toggles */}
              <div className="space-y-3">
                <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-wider font-mono">Master Channels</h4>
                <div className="space-y-2">
                  <label className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer">
                    <div>
                      <p className="text-xs font-extrabold text-slate-800">Email Notifications</p>
                      <p className="text-[11px] text-slate-500">Send notifications via real outbound SendGrid emails</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={tempPrefs.emailNotifications}
                      onChange={(e) => setTempPrefs({ ...tempPrefs, emailNotifications: e.target.checked })}
                      className="w-4 h-4 rounded text-[#0094c8] focus:ring-[#0094c8]"
                    />
                  </label>

                  <label className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer">
                    <div>
                      <p className="text-xs font-extrabold text-slate-800">In-Portal Notifications</p>
                      <p className="text-[11px] text-slate-500">Deliver messages to the in-app portal messages inbox</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={tempPrefs.inAppNotifications}
                      onChange={(e) => setTempPrefs({ ...tempPrefs, inAppNotifications: e.target.checked })}
                      className="w-4 h-4 rounded text-[#0094c8] focus:ring-[#0094c8]"
                    />
                  </label>

                  <label className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer">
                    <div>
                      <p className="text-xs font-extrabold text-slate-800">Instant Real-Time Alerts</p>
                      <p className="text-[11px] text-slate-500">Deliver notifications immediately upon system events</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={tempPrefs.instantEmailAlerts}
                      onChange={(e) => setTempPrefs({ ...tempPrefs, instantEmailAlerts: e.target.checked })}
                      className="w-4 h-4 rounded text-[#0094c8] focus:ring-[#0094c8]"
                    />
                  </label>
                </div>
              </div>

              {/* Specific Categories */}
              <div className="space-y-3">
                <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-wider font-mono">Category Permissions</h4>
                <div className="space-y-2">
                  {[
                    { key: 'ferpaConsentAlerts', title: 'FERPA Consent Releases', desc: 'Parent digital consent signature events' },
                    { key: 'newUserRegistrationAlerts', title: 'New User Signups', desc: 'New student, employer, or advisor accounts' },
                    { key: 'opportunityCreatedAlerts', title: 'Opportunity Postings', desc: 'New internships or job shadows added' },
                    { key: 'applicationAlerts', title: 'Applications & Placements', desc: 'Student submissions and status changes' },
                    { key: 'hoursApprovalAlerts', title: 'Hours Approvals', desc: 'Attendance timesheet approvals' },
                    { key: 'evaluationAlerts', title: 'Competency Evaluations', desc: 'Completed workplace supervisor reviews' },
                    { key: 'systemSecurityAlerts', title: 'Security & Auth Alerts', desc: 'Password resets and administrative changes' }
                  ].map((cat) => (
                    <label key={cat.key} className="flex items-center justify-between p-3 rounded-xl border border-slate-200/80 bg-slate-50/50 hover:bg-slate-50 cursor-pointer">
                      <div>
                        <p className="text-xs font-bold text-slate-800">{cat.title}</p>
                        <p className="text-[10px] text-slate-500">{cat.desc}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={Boolean(tempPrefs[cat.key as keyof NotificationPreferences])}
                        onChange={(e) => setTempPrefs({ ...tempPrefs, [cat.key]: e.target.checked })}
                        className="w-4 h-4 rounded text-[#0094c8] focus:ring-[#0094c8]"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-end space-x-2">
              <button
                onClick={() => setEditingUserPrefs(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUserPrefs}
                disabled={isSavingPrefs}
                className="px-5 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shadow-sm disabled:opacity-50"
              >
                {isSavingPrefs ? 'Saving Changes...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* MODAL 3: SEND TEST NOTIFICATION MODAL                */}
      {/* ==================================================== */}
      {showTestModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <form onSubmit={handleSendTestEmail} className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-xl w-full flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-slate-50/50">
              <div>
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono">
                  Test & Verification Dispatch
                </span>
                <h3 className="text-lg font-black text-slate-900 tracking-tight mt-1">
                  Send Test Notification Email
                </h3>
                <p className="text-xs text-slate-500">
                  Verify real SendGrid outbound transmission and preference filtering rules.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-200/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 block mb-1">
                  Recipient Email Address *
                </label>
                <input
                  type="email"
                  required
                  placeholder="e.g. user@school.edu or admin@domain.com"
                  value={testRecipientEmail}
                  onChange={(e) => setTestRecipientEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-[#0094c8] outline-none"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 block mb-1">
                  Alert Category
                </label>
                <select
                  value={testCategory}
                  onChange={(e) => setTestCategory(e.target.value as any)}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-[#0094c8] outline-none"
                >
                  <option value="ferpaConsentAlerts">FERPA Consent Release</option>
                  <option value="newUserRegistrationAlerts">New User Signup</option>
                  <option value="opportunityCreatedAlerts">Opportunity Posting Alert</option>
                  <option value="applicationAlerts">Application & Placement Notice</option>
                  <option value="hoursApprovalAlerts">Hours Approval Notification</option>
                  <option value="evaluationAlerts">Competency Evaluation Notice</option>
                  <option value="systemSecurityAlerts">Security & Password Alert</option>
                  <option value="standardAlerts">Standard Platform Broadcast</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 block mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  required
                  value={testSubject}
                  onChange={(e) => setTestSubject(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-[#0094c8] outline-none"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 block mb-1">
                  Email Message Body
                </label>
                <textarea
                  rows={4}
                  required
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-[#0094c8] outline-none"
                />
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSendingTest}
                className="px-5 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shadow-sm flex items-center space-x-2 disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{isSendingTest ? 'Dispatching...' : 'Dispatch Test'}</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
